import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft,
  FileText,
  User,
  Phone,
  Mail,
  Tag,
  Clock,
  ArrowRightLeft,
  MessageSquare,
  CheckSquare,
  Zap,
  Trash2,
  Link as LinkIcon,
  AlertCircle,
  Search,
} from 'lucide-react';

interface TimelineEntry {
  type: 'event' | 'note' | 'task';
  subtype: string;
  timestamp: string;
  created_by: number | null;
  detail: string | null;
  data: any;
}

interface LeadData {
  kommo_id: number;
  name: string;
  price: string;
  status_id: number;
  status_name?: string;
  pipeline_id: number;
  responsible_user_id: number;
  responsible_user_name: string;
  is_deleted: boolean;
  closed_at: string | null;
  kommo_created_at: string;
  kommo_updated_at: string;
  tags: string[];
}

interface ContactData {
  kommo_id: number;
  name: string;
  phone: string | null;
  email: string | null;
}

interface LeadTimelineResponse {
  lead: LeadData;
  contacts: ContactData[];
  timeline: TimelineEntry[];
  seller_map: Record<string, string>;
  status_map?: Record<string, string>;
}

const EVENT_ICONS: Record<string, { icon: React.ElementType; color: string }> = {
  lead_status_changed: { icon: ArrowRightLeft, color: 'bg-blue-100 text-blue-600' },
  entity_responsible_changed: { icon: User, color: 'bg-violet-100 text-violet-600' },
  entity_tag_added: { icon: Tag, color: 'bg-amber-100 text-amber-600' },
  common_note_added: { icon: MessageSquare, color: 'bg-emerald-100 text-emerald-600' },
  entity_linked: { icon: LinkIcon, color: 'bg-cyan-100 text-cyan-600' },
  lead_added: { icon: Zap, color: 'bg-green-100 text-green-600' },
  outgoing_mail: { icon: Mail, color: 'bg-indigo-100 text-indigo-600' },
  incoming_mail: { icon: Mail, color: 'bg-sky-100 text-sky-600' },
  lead_deleted: { icon: Trash2, color: 'bg-red-100 text-red-600' },
  note: { icon: MessageSquare, color: 'bg-emerald-100 text-emerald-600' },
  task: { icon: CheckSquare, color: 'bg-orange-100 text-orange-600' },
};

const EVENT_LABELS: Record<string, string> = {
  lead_status_changed: 'Changement de statut',
  entity_responsible_changed: 'Changement de responsable',
  entity_tag_added: 'Tag ajoute',
  entity_tag_deleted: 'Tag retire',
  common_note_added: 'Note ajoutee',
  common_note_deleted: 'Note supprimee',
  entity_linked: 'Liaison',
  entity_unlinked: 'Deliaison',
  name_field_changed: 'Nom modifie',
  lead_added: 'Lead cree',
  lead_deleted: 'Lead supprime',
  lead_restored: 'Lead restaure',
  outgoing_mail: 'Email envoye',
  incoming_mail: 'Email recu',
  entity_merged: 'Fusion',
  entity_direct_message: 'Message direct',
  service_note_added: 'Note service',
  common: 'Note',
  amomail_message: 'Email',
  task: 'Tache',
};

const KommoLeadTimeline: React.FC = () => {
  const { kommoId } = useParams<{ kommoId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<LeadTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState(kommoId || '');

  const fetchTimeline = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiCall<{ success: boolean; data: LeadTimelineResponse; message?: string }>(
        token,
        `/kommo/analytics/lead/${id}/timeline`
      );
      if (res.success) {
        setData(res.data);
      } else {
        toast.error(res.message || 'Lead non trouve');
        setData(null);
      }
    } catch (err: any) {
      const msg = err?.message?.includes('404') ? 'Lead non trouve' : 'Erreur lors du chargement du lead';
      toast.error(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setSearchId(kommoId || '');
    if (kommoId) {
      fetchTimeline(kommoId);
    } else {
      setLoading(false);
    }
  }, [kommoId, fetchTimeline]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchId.trim()) fetchTimeline(searchId.trim());
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getEventIcon = (type: string, subtype: string) => {
    if (type === 'note') return EVENT_ICONS.note || EVENT_ICONS.common_note_added;
    if (type === 'task') return EVENT_ICONS.task;
    return EVENT_ICONS[subtype] || { icon: Zap, color: 'bg-slate-100 text-slate-600' };
  };

  const getSellerName = (id: number | null) => {
    if (id === null || id === undefined || !data) return 'Systeme';
    return data.seller_map[String(id)] || `#${id}`;
  };

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/kommo/leads">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Timeline Lead {data ? `#${data.lead.kommo_id}` : ''}
            </h1>
            <p className="text-sm text-slate-500 font-medium mt-0.5">
              Historique complet des evenements
            </p>
          </div>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            placeholder="Kommo ID..."
            className="w-32"
          />
          <Button type="submit" variant="outline" size="sm">
            <Search className="h-3.5 w-3.5" />
          </Button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : data ? (
        <>
          {/* Lead info */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900">
                  {data.lead.name || 'Lead sans nom'}
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Cree le {formatDate(data.lead.kommo_created_at)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Prix</span>
                  <span className="font-medium">
                    {data.lead.price ? `${parseFloat(data.lead.price).toLocaleString('fr-FR')} EUR` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Statut</span>
                  <Badge variant="secondary">
                    {data.lead.status_name || data.status_map?.[String(data.lead.status_id)] || `#${data.lead.status_id}`}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Responsable</span>
                  <span className="font-medium">{data.lead.responsible_user_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Supprime</span>
                  <Badge variant={data.lead.is_deleted ? 'destructive' : 'secondary'}>
                    {data.lead.is_deleted ? 'Oui' : 'Non'}
                  </Badge>
                </div>
                {data.lead.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {data.lead.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contacts */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-bold text-slate-900">Contacts associes</CardTitle>
                <CardDescription className="text-xs text-slate-500">{data.contacts.length} contact(s)</CardDescription>
              </CardHeader>
              <CardContent>
                {data.contacts.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucun contact lie</p>
                ) : (
                  <div className="space-y-3">
                    {data.contacts.map((c) => (
                      <div key={c.kommo_id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="font-medium text-sm text-slate-900">{c.name || 'Sans nom'}</p>
                        {c.phone && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </p>
                        )}
                        {c.email && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" /> {c.email}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Timeline */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base font-bold text-slate-900">Chronologie</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                {data.timeline.length} evenements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />

                <div className="space-y-4">
                  {data.timeline.map((entry, index) => {
                    const { icon: Icon, color } = getEventIcon(entry.type, entry.subtype);
                    const label = entry.type === 'event'
                      ? EVENT_LABELS[entry.subtype] || entry.subtype
                      : entry.type === 'note'
                        ? EVENT_LABELS[entry.subtype] || 'Note'
                        : 'Tache';

                    return (
                      <div key={`${entry.type}-${entry.subtype}-${entry.timestamp}-${index}`} className="relative flex items-start gap-4 pl-2">
                        <div className={cn('relative z-10 flex items-center justify-center w-7 h-7 rounded-full border-2 border-white', color)}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">{label}</span>
                            <Badge variant="outline" className="text-[10px]">{entry.type}</Badge>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(entry.timestamp)}
                            <span className="mx-1">-</span>
                            {getSellerName(entry.created_by)}
                          </p>
                          {/* Detail from backend */}
                          {entry.detail && (
                            <div className="mt-1.5 text-xs text-slate-600 bg-slate-50 p-2 rounded">
                              {entry.detail}
                              {entry.type === 'task' && entry.data?.is_completed && (
                                <Badge variant="default" className="ml-2 text-[10px]">Termine</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Entrez un Kommo ID pour afficher la timeline</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KommoLeadTimeline;
